import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, AlertTriangle, CheckCircle2, Timer, Layers, Users, RefreshCw, Bell } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const TONES = {
  slate: 'bg-slate-100 text-slate-600',
  emerald: 'bg-emerald-100 text-emerald-600',
  indigo: 'bg-indigo-100 text-indigo-600',
  red: 'bg-red-100 text-red-600',
  blue: 'bg-blue-100 text-blue-600',
};

const Kpi = ({ icon: Icon, label, value, tone = 'slate', testid }) => (
  <div className="rounded-2xl p-4 bg-white border border-slate-200 shadow-sm" data-testid={testid}>
    <div className={`inline-flex p-2 rounded-lg mb-2 ${TONES[tone] || TONES.slate}`}>
      <Icon className="w-5 h-5" />
    </div>
    <p className="text-2xl font-bold text-slate-800">{value}</p>
    <p className="text-xs text-slate-500 mt-0.5">{label}</p>
  </div>
);

export default function OrderDashboard() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [data, setData] = useState(null);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [dash, dig] = await Promise.all([
        api.get('/order-jobs/dashboard'),
        api.get('/order-jobs/digest/latest').catch(() => ({ data: null })),
      ]);
      setData(dash.data);
      setDigest(dig.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load dashboard');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const sendDigestNow = async () => {
    setSending(true);
    try {
      const res = await api.post('/order-jobs/digest/run');
      setDigest(res.data);
      toast.success(res.data.total_overdue > 0
        ? `Digest sent — ${res.data.total_overdue} overdue job(s)`
        : 'Digest run — nothing overdue right now 🎉');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send digest');
    } finally { setSending(false); }
  };

  const k = data?.kpis || {};

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="order-dashboard-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-slate-100" data-testid="dashboard-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-indigo-600" /> Workshop Dashboard
            </h1>
            <p className="text-sm text-slate-500">On-time delivery, bottlenecks & vendor performance</p>
          </div>
        </div>
        <button onClick={load} className="p-2.5 rounded-full bg-white border border-slate-200" data-testid="dashboard-refresh-btn">
          <RefreshCw className={`w-5 h-5 text-slate-600 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm">Loading…</p>
      ) : !data ? (
        <p className="text-slate-400 text-sm">No data.</p>
      ) : (
        <div className="space-y-8">
          {/* Overdue digest */}
          <div className="rounded-2xl p-4 border border-red-200 bg-red-50" data-testid="overdue-digest-card">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-red-100"><Bell className="w-5 h-5 text-red-600" /></div>
                <div>
                  <h2 className="text-sm font-semibold text-red-700">Overdue Digest</h2>
                  {digest && digest.total_overdue > 0 ? (
                    <p className="text-sm text-slate-700 mt-0.5" data-testid="digest-summary">
                      <b>{digest.total_overdue}</b> job(s) overdue across <b>{digest.orders_affected}</b> order(s) · oldest <b>{digest.oldest_days}d</b>
                    </p>
                  ) : (
                    <p className="text-sm text-slate-600 mt-0.5" data-testid="digest-summary">Nothing overdue right now 🎉</p>
                  )}
                  <p className="text-xs text-slate-400 mt-0.5">
                    {digest?.generated_at ? `Last run ${new Date(digest.generated_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })} · auto-sent daily 9am` : 'Auto-sent daily at 9am'}
                  </p>
                </div>
              </div>
              {currentUser?.role === 'admin' && (
                <button onClick={sendDigestNow} disabled={sending}
                  className="shrink-0 text-xs px-3 py-2 rounded-full bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50"
                  data-testid="send-digest-btn">
                  {sending ? 'Sending…' : 'Send now'}
                </button>
              )}
            </div>
            {digest && digest.by_owner && digest.by_owner.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="digest-by-owner">
                {digest.by_owner.map((o, i) => (
                  <span key={i} className="text-xs bg-white border border-red-200 rounded-full px-2.5 py-1 text-slate-700">
                    {o.name} · <b className="text-red-600">{o.count}</b> overdue (max {o.max_days}d)
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* KPI grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="dashboard-kpis">
            <Kpi icon={TrendingUp} label="On-time delivery" tone="emerald" testid="kpi-ontime"
              value={k.on_time_pct == null ? '—' : `${k.on_time_pct}%`} />
            <Kpi icon={Timer} label="Avg turnaround" tone="indigo" testid="kpi-turnaround"
              value={k.avg_turnaround_days == null ? '—' : `${k.avg_turnaround_days}d`} />
            <Kpi icon={AlertTriangle} label="Overdue now" tone="red" testid="kpi-overdue" value={k.overdue ?? 0} />
            <Kpi icon={Layers} label="Active jobs" tone="blue" testid="kpi-active" value={k.active ?? 0} />
            <Kpi icon={CheckCircle2} label="Completed" tone="emerald" testid="kpi-completed" value={k.completed ?? 0} />
            <Kpi icon={Layers} label="Total jobs" tone="slate" testid="kpi-total" value={k.total_jobs ?? 0} />
          </div>

          {/* Bottlenecks */}
          <div>
            <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" /> Bottlenecks by stage
            </h2>
            {(!data.bottlenecks || data.bottlenecks.length === 0) ? (
              <p className="text-sm text-slate-400">No jobs yet.</p>
            ) : (
              <div className="space-y-2" data-testid="dashboard-bottlenecks">
                {data.bottlenecks.map((b) => {
                  const load = b.active + b.done;
                  const overduePct = load ? (b.overdue / (b.active || 1)) * 100 : 0;
                  return (
                    <div key={b.checkpoint} className="bg-white border border-slate-200 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-medium text-slate-800">{b.checkpoint}</span>
                        <span className="text-xs text-slate-500">{b.active} active · {b.done} done{b.overdue ? ` · ${b.overdue} overdue` : ''}</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-red-500" style={{ width: `${Math.min(overduePct, 100)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Vendor performance */}
          <div>
            <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-indigo-500" /> Vendor / karigar performance
            </h2>
            {(!data.vendors || data.vendors.length === 0) ? (
              <p className="text-sm text-slate-400">No jobs assigned yet.</p>
            ) : (
              <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white" data-testid="dashboard-vendors">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                      <th className="px-3 py-2">Owner</th>
                      <th className="px-3 py-2 text-right">Jobs</th>
                      <th className="px-3 py-2 text-right">Done</th>
                      <th className="px-3 py-2 text-right">On-time</th>
                      <th className="px-3 py-2 text-right">Avg days</th>
                      <th className="px-3 py-2 text-right">Overdue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.vendors.map((v, i) => (
                      <tr key={i} className="border-b border-slate-50 last:border-0" data-testid="vendor-row">
                        <td className="px-3 py-2">
                          <span className="font-medium text-slate-800">{v.name}</span>
                          {v.kind && <span className="ml-1 text-[10px] text-slate-400 uppercase">{v.kind}</span>}
                        </td>
                        <td className="px-3 py-2 text-right">{v.total}</td>
                        <td className="px-3 py-2 text-right">{v.done}</td>
                        <td className={`px-3 py-2 text-right font-medium ${v.on_time_pct == null ? 'text-slate-400' : v.on_time_pct >= 80 ? 'text-emerald-600' : v.on_time_pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                          {v.on_time_pct == null ? '—' : `${v.on_time_pct}%`}
                        </td>
                        <td className="px-3 py-2 text-right">{v.avg_turnaround_days == null ? '—' : v.avg_turnaround_days}</td>
                        <td className={`px-3 py-2 text-right ${v.overdue ? 'text-red-600 font-semibold' : ''}`}>{v.overdue || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
